import React, { useState } from 'react';

export interface CustomerFormValues {
  name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  country: string;
  zipcode: string;
  type: string;
}

const emptyForm: CustomerFormValues = {
  name: '',
  email: '',
  phone: '',
  address: '',
  city: '',
  state: '',
  country: '',
  zipcode: '',
  type: '',
};

interface AddCustomerFormProps {
  onSave: (values: CustomerFormValues) => void;
  onCancel: () => void;
}

const AddCustomerForm: React.FC<AddCustomerFormProps> = ({ onSave, onCancel }) => {
  const [form, setForm] = useState<CustomerFormValues>(emptyForm);

  const updateField = (field: keyof CustomerFormValues, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    if (!form.name.trim() || !form.email.trim()) return;
    onSave(form);
  };

  return (
    <div className="flex flex-col" style={{ maxHeight: 'calc(100vh - 400px)' }}>
      {/* Scrollable Form Fields */}
      <div className="overflow-y-auto flex-1 space-y-4 pr-2">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => updateField('name', e.target.value)}
              placeholder="Enter name"
              className="w-full px-3 py-2 rounded-lg bg-[#1a1a1a] border border-gray-600 text-white placeholder-gray-500 focus:outline-none focus:border-brand-primary text-sm"
            />
          </div>
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => updateField('email', e.target.value)}
              placeholder="Enter email"
              className="w-full px-3 py-2 rounded-lg bg-[#1a1a1a] border border-gray-600 text-white placeholder-gray-500 focus:outline-none focus:border-brand-primary text-sm"
            />
          </div>
        </div>

        <div>
          <label className="block text-gray-300 text-sm font-medium mb-2">Phone</label>
          <input
            type="tel"
            value={form.phone}
            onChange={(e) => updateField('phone', e.target.value)}
            placeholder="Enter phone number"
            className="w-full px-3 py-2 rounded-lg bg-[#1a1a1a] border border-gray-600 text-white placeholder-gray-500 focus:outline-none focus:border-brand-primary text-sm"
          />
        </div>

        <div>
          <label className="block text-gray-300 text-sm font-medium mb-2">Address</label>
          <input
            type="text"
            value={form.address}
            onChange={(e) => updateField('address', e.target.value)}
            placeholder="Street address"
            className="w-full px-3 py-2 rounded-lg bg-[#1a1a1a] border border-gray-600 text-white placeholder-gray-500 focus:outline-none focus:border-brand-primary text-sm"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">City</label>
            <input
              type="text"
              value={form.city}
              onChange={(e) => updateField('city', e.target.value)}
              placeholder="City"
              className="w-full px-3 py-2 rounded-lg bg-[#1a1a1a] border border-gray-600 text-white placeholder-gray-500 focus:outline-none focus:border-brand-primary text-sm"
            />
          </div>
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">State</label>
            <input
              type="text"
              value={form.state}
              onChange={(e) => updateField('state', e.target.value)}
              placeholder="State"
              className="w-full px-3 py-2 rounded-lg bg-[#1a1a1a] border border-gray-600 text-white placeholder-gray-500 focus:outline-none focus:border-brand-primary text-sm"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">Country</label>
            <input
              type="text"
              value={form.country}
              onChange={(e) => updateField('country', e.target.value)}
              placeholder="Country"
              className="w-full px-3 py-2 rounded-lg bg-[#1a1a1a] border border-gray-600 text-white placeholder-gray-500 focus:outline-none focus:border-brand-primary text-sm"
            />
          </div>
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">Zipcode</label>
            <input
              type="text"
              value={form.zipcode}
              onChange={(e) => updateField('zipcode', e.target.value)}
              placeholder="Zipcode"
              className="w-full px-3 py-2 rounded-lg bg-[#1a1a1a] border border-gray-600 text-white placeholder-gray-500 focus:outline-none focus:border-brand-primary text-sm"
            />
          </div>
        </div>

        <div>
          <label className="block text-gray-300 text-sm font-medium mb-2">Type</label>
          <input
            type="text"
            value={form.type}
            onChange={(e) => updateField('type', e.target.value)}
            placeholder="Customer type"
            className="w-full px-3 py-2 rounded-lg bg-[#1a1a1a] border border-gray-600 text-white placeholder-gray-500 focus:outline-none focus:border-brand-primary text-sm"
          />
        </div>
      </div>

      {/* Sticky Buttons */}
      <div className="flex gap-3 pt-4 mt-4 border-t border-gray-700 sticky bottom-0 bg-[#2a2a2a]">
        <button
          onClick={handleSave}
          disabled={!form.name.trim() || !form.email.trim()}
          className="flex-1 py-2 rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer bg-brand-primary hover:bg-[#ee4423] text-white"
        >
          Save Customer
        </button>
        <button
          onClick={onCancel}
          className="px-6 py-2 rounded-lg font-medium transition-all bg-gray-600 hover:bg-gray-700 text-white cursor-pointer"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default AddCustomerForm;
